import { useMutation } from "@apollo/client";
import CloseIcon from "@mui/icons-material/Close";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
} from "@mui/material";
import { Button, Notification } from "pepsico-ds";
import { useState } from "react";
import { configureMultiFileDownloadMutation } from "../../../graphql/mutations/codeGroupMutations";
import { parseGraphQLError } from "../../../utils/commons";
import JsonFormsWrapper from "../../jsonForms/jsonFormsWrapper/JsonFormsWrapper";
import {
  codeGroupMultipleFileDonwloadJsonSchema,
  codeGroupMultipleFileDonwloadUISchema,
  extraValidateMultiFile,
  isMultiFileFormIncomplete,
  validateMultiFileSchema,
} from "./codeGroupMultiFileDownloadUtils";

export default function CodeGroupMultiFileDownloadModal({
  showModal,
  setShowModal,
  codeGroupId,
  codesCount,
}) {
  const [modalToast, setModalToast] = useState([]);
  const [formData, setFormData] = useState({
    folders: [{}],
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [configureMultiFileDownload, { loading }] = useMutation(
    configureMultiFileDownloadMutation
  );

  const onCloseModal = () => {
    setShowModal(false);
    setFormData({
      folders: [{}],
    });
    setModalToast([]);
  };

  const isFormIncomplete = () => isMultiFileFormIncomplete(formData);

  const extraValidate = () => extraValidateMultiFile(formData, codesCount);

  const validateData = (payload) => {
    const { valid } = validateMultiFileSchema(payload);
    if (!valid) {
      setModalToast([
        {
          type: "error",
          id: "1",
          text: "Please fill all the required fields.",
        },
      ]);
      return false;
    }
    return true;
  };

  const onDownloadClick = async () => {
    try {
      setModalToast([]);
      const extra = extraValidate();
      if (!extra.valid) {
        setModalToast([
          {
            type: "error",
            id: "1",
            text: extra.message,
          },
        ]);
        return;
      }
      const tokenInfo = localStorage.getItem("okta-token-storage");
      const { accessToken } = JSON.parse(tokenInfo);
      const userInfo = accessToken?.claims;

      const payload = {
        ...formData,
        notificationEmailAddress: userInfo?.email,
        createdBy: userInfo?.gpid,
        codeGroupId,
      };
      if (!validateData(payload)) return;
      const response = await configureMultiFileDownload({
        variables: { input: payload },
      });
      if (response?.data?.configureMultiFileDownload?.status === "IN_PROGRESS") {
        setModalToast([
          {
            type: "info",
            id: "1",
            title:
              response?.data?.configureMultiFileDownload?.message ||
              "Export initiated successfully.",
            text: "You can see details with download link in the view screen.",
          },
        ]);
        setIsSubmitted(true);
      } else {
        setModalToast([
          {
            type: "error",
            id: "1",
            text: response?.data?.configureMultiFileDownload?.message,
          },
        ]);
        setIsSubmitted(false);
      }
    } catch (error) {
      console.error(error);
      setModalToast([
        {
          type: "error",
          id: "1",
          text: parseGraphQLError(error),
        },
      ]);
      setIsSubmitted(false);
    }
  };

  return (
    <Dialog
      className="createProduct-modal"
      open={showModal}
      scroll="paper"
      fullWidth
    >
      <DialogTitle>
        <Grid container justifyContent="space-between" direction="row">
          <Grid item xs={10}>
            <h2
              data-testid="create-code-group-modal"
              style={{ marginLeft: "18%", textAlign: "center" }}
            >
              Download Code Group
            </h2>
          </Grid>
          <Grid item xs={1} display="flex" justifyContent="flex-end">
            <CloseIcon
              onClick={onCloseModal}
              className="cursor-pointer"
              data-testid="closeIcon"
            />
          </Grid>
        </Grid>
      </DialogTitle>

      <DialogContent
        sx={{
          overflowX: "hidden",
        }}
      >
        <div>
          {modalToast?.length > 0 && (
            <Notification
              type={modalToast?.[0]?.type}
              title={modalToast?.[0]?.title || ""}
              text={modalToast?.[0]?.text || ""}
              primaryActionLabel={""}
              primaryActionOnClick={() => setModalToast([])}
            />
          )}
        </div>
        <div>
          <p>Select the number of folders/files to divide your codes into.</p>
          <p>
            For each file you generate, a folder will be created to contain it. The
            total number of codes will be evenly distributed across all generated
            files.
          </p>
          <p>Each file can have a maximun of 1,000,000,000 codes.</p>
        </div>
        <div className="my-5 create-modal-wrapper">
          <JsonFormsWrapper
            jsonschema={codeGroupMultipleFileDonwloadJsonSchema}
            uischema={codeGroupMultipleFileDonwloadUISchema}
            data={formData}
            onChange={(data) => {
              setFormData(data);
            }}
          />
        </div>
      </DialogContent>
      <DialogActions className="mt-5 display-flex justify-content-end align-items-center">
        <Button
          variant="tertiary"
          className="jufstify-content-space-around"
          disabled={false}
          onClick={onCloseModal}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          className="jufstify-content-space-around"
          disabled={isFormIncomplete() || isSubmitted}
          isLoading={loading}
          onClick={onDownloadClick}
        >
          Download
        </Button>
      </DialogActions>
    </Dialog>
  );
}
